import { useEffect, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { Drawer } from '@scholastic/mockingjay';
import FilterIcon from '@data/assets/filter.svg';
import ListMenuIcon from '@data/assets/listMenu.svg';
import {
  AdminReportNavMenu,
  type AdminReportNavProps,
} from '@data/components/report/AdminReportNav/AdminReportNav';

/**
 * Below the laptop breakpoint the filters and the report nav have no room of
 * their own, so each collapses to an anchor that opens a drawer holding the
 * same controls. Above it both sit on the page and neither drawer exists.
 */
const TRIGGER_CLASS =
  'flex size-8 shrink-0 items-center justify-center rounded-sm text-neutral-1300 hover:bg-neutral-100 laptop:hidden';

/** The filter card's anchor, at the top right of the collapsed card. */
export function AdminFilterDrawer({ children }: { children: ReactNode }) {
  return (
    <Drawer
      size="xs"
      direction="right"
      title="Filters"
      subtitle="Filter reports"
      trigger={
        <button
          type="button"
          aria-label="Filter reports"
          className={TRIGGER_CLASS}
        >
          <FilterIcon aria-hidden className="size-4" />
        </button>
      }
    >
      <div className="flex flex-col gap-4 p-4">{children}</div>
    </Drawer>
  );
}

/** The nav's anchor, beside the product title. It opens from the left, the
 * side the nav sits on above the breakpoint. */
export function AdminNavDrawer({ drilldown }: AdminReportNavProps) {
  const [open, setOpen] = useState(false);
  const { pathname, search } = useLocation();

  useEffect(() => setOpen(false), [pathname, search]);

  return (
    <Drawer
      open={open}
      onOpenChange={setOpen}
      size="xs"
      direction="left"
      title="Products & Reports"
      subtitle="Navigate to a product report."
      trigger={
        <button
          type="button"
          aria-label="Products and reports"
          className={TRIGGER_CLASS}
        >
          <ListMenuIcon aria-hidden className="size-4" />
        </button>
      }
    >
      {/* The location effect misses a click on the active report. */}
      <AdminReportNavMenu
        drilldown={drilldown}
        onNavigate={() => setOpen(false)}
      />
    </Drawer>
  );
}
